if (!customElements.get('modal-media-gallery')) {
  customElements.define(
    'modal-media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLElement | null} */
        this.gallery = null;

        /** @type {HTMLElement | null} */
        this.scrollbar = null;

        /** @type {HTMLElement | null} */
        this.thumbnailsContainer = null;

        /** @type {HTMLElement | null} */
        this.closeControlsButton = null;

        /** @type {HTMLElement | null} */
        this.openControlsButton = null;

        /** @type {HTMLElement | null} */
        this.mobileControls = null;

        /** @type {HTMLElement[]} */
        this.zoomInButtons = [];

        /** @type {HTMLElement[]} */
        this.zoomOutButtons = [];

        /** @type {HTMLElement[]} */
        this.zoomValueEls = [];

        /** @type {import('swiper').Swiper | null} */
        this.instance = null;

        /** @type {HTMLImageElement[]} */
        this.images = [];

        this._zoomLevels = [1, 1.5, 2, 2.5];
        this._zoom = this._zoomLevels[0];
        this._minZoom = this._zoomLevels[0];
        this._maxZoom = this._zoomLevels[this._zoomLevels.length - 1];

        this._panX = 0;
        this._panY = 0;

        this._isDragging = false;
        this._activePointerId = null;
        this._dragStartClientX = 0;
        this._dragStartClientY = 0;
        this._dragStartPanX = 0;
        this._dragStartPanY = 0;

        /** @type {boolean} - True when pointer moved during drag (used to distinguish click from drag) */
        this._dragDidMove = false;

        /** @type {'up' | 'down'} - Direction when cycling zoom via image click (desktop) */
        this._zoomCycleDirection = 'up';

        /** @type {boolean} - Skip next image click zoom when it was a drag release */
        this._imageClickZoomBlocked = false;

        this._boundCaptureZoomIndex = this._captureZoomIndex.bind(this);
        this._boundHandleZoomInClick = this._handleZoomInClick.bind(this);
        this._boundHandleImageClickZoom = this._handleImageClickZoom.bind(this);
        this._boundHandleZoomOutClick = this._handleZoomOutClick.bind(this);
        this._boundPreloadThumbnailsAfterLoad = this._preloadThumbnailsAfterLoad.bind(this);
        this._boundScheduleModalImagePreloadAfterLoad = this._scheduleModalImagePreloadAfterLoad.bind(this);
        this._boundHandleModalTrapActivate = this._handleModalTrapActivate.bind(this);
        this._boundHandleModalTrapDeactivate = this._handleModalTrapDeactivate.bind(this);
        this._boundHandleProductToggleBreakpoint = this._handleProductToggleBreakpoint.bind(this);

        this._boundOnPointerDown = this._onPointerDown.bind(this);
        this._boundOnPointerMove = this._onPointerMove.bind(this);
        this._boundOnPointerUp = this._onPointerUp.bind(this);

        this._boundHandleThumbnailClick = this._handleThumbnailClick.bind(this);
        this._boundHandleCloseControlsClick = this._handleCloseControlsClick.bind(this);
        this._boundHandleOpenControlsClick = this._handleOpenControlsClick.bind(this);

        /** @type {number | ReturnType<typeof setTimeout> | null} */
        this._modalPreloadDelayTimeout = null;

        /** @type {number | ReturnType<typeof setTimeout> | null} */
        this._modalPreloadIdleCallback = null;

        /** @type {AbortController | null} */
        this._productToggleAbort = null;

        /** @type {boolean} */
        this._productToggleActive = false;

        /** @type {HTMLElement | null} */
        this._productToggleGalleryContent = null;

        /** @type {{ el: HTMLElement; text: string }[] | null} */
        this._productToggleButtonOriginals = null;

        /**
         * @param {DragEvent} e - Drag event.
         * @returns {void}
         */
        this._boundPreventDragStart = (e) => e.preventDefault();
      }

      /** Caches DOM references when children are available (not in constructor). */
      #cacheDomRefs() {
        this.gallery = this.querySelector('[data-gallery]');
        this.scrollbar = this.querySelector('[data-gallery-scrollbar]');
        this.thumbnailsContainer = this.querySelector('[data-gallery-thumbnails]');
        this.closeControlsButton = this.querySelector('[data-modal-gallery-controls-close]');
        this.openControlsButton = this.querySelector('[data-modal-gallery-controls-open]');
        this.mobileControls =
          this.querySelector('[data-gallery-thumbnails-wrapper]') ??
          this.querySelector('.modal-media-gallery-thumbnails-wrapper');
        this.zoomInButtons = Array.from(this.querySelectorAll('[data-modal-gallery-zoom-in]'));
        this.zoomOutButtons = Array.from(this.querySelectorAll('[data-modal-gallery-zoom-out]'));
        this.zoomValueEls = Array.from(this.querySelectorAll('[data-modal-gallery-zoom-value]'));
        this.images = /** @type {HTMLImageElement[]} */ (Array.from(this.querySelectorAll('img')));
      }

      connectedCallback() {
        this.#cacheDomRefs();

        document.addEventListener('click', this._boundCaptureZoomIndex, true);

        this.addEventListener('dragstart', this._boundPreventDragStart);

        this.addEventListener('pointerdown', this._boundOnPointerDown);
        this.addEventListener('click', this._boundHandleImageClickZoom, true);
        window.addEventListener('pointermove', this._boundOnPointerMove);
        window.addEventListener('pointerup', this._boundOnPointerUp);
        window.addEventListener('pointercancel', this._boundOnPointerUp);
        window.addEventListener('modal-trap-activate', this._boundHandleModalTrapActivate);

        const modalSwiper = window.swiper.Swiper;

        if (this.gallery && this.scrollbar) {
          this.instance = new modalSwiper(this.gallery, {
            modules: [window.swiper.Scrollbar, window.swiper.Navigation, window.swiper.EffectFade],
            cssMode: false,
            allowTouchMove: false,
            observer: true,
            observeParents: true,
            effect: 'fade',
            fadeEffect: {
              crossFade: true,
            },
            slidesPerView: 1,
            spaceBetween: 0,
            speed: 250,
            on: {
              slideChange: (s) => {
                window.gtag?.('event', 'media_gallery_slide_change', {
                  slide_index: s.activeIndex + 1,
                  page_path_clean: window.location.pathname.replace(/\/$/, ''),
                });

                for (const image of this.images) {
                  if (image) image.setAttribute('loading', 'eager');
                }

                this.#setActiveThumbnail(s.activeIndex);
                this.#emitZoomIndexChanged(s.activeIndex);

                this.#resetAllSlidesZoom();
                this.#resetPan();
                this.#setZoom(1);
              },
            },
          });

          this.#syncInitialSlideFromTrigger();
          this.#setActiveThumbnail(this.instance.activeIndex);
          this.#bindThumbnails();
          this.#emitZoomIndexChanged(this.instance.activeIndex);
        }

        this.#bindMobileControls();
        this.#bindZoomControls();
        this.#bindProductToggle();

        this.#setZoom(this._zoom);

        if (document.readyState === 'complete') {
          this._preloadThumbnailsAfterLoad();
          this._scheduleModalImagePreloadAfterLoad();
        } else {
          window.addEventListener('load', this._boundPreloadThumbnailsAfterLoad, { once: true });
          window.addEventListener('load', this._boundScheduleModalImagePreloadAfterLoad, { once: true });
        }
      }

      disconnectedCallback() {
        if (this._productToggleAbort) {
          this._productToggleAbort.abort();
          this._productToggleAbort = null;
        }

        document.removeEventListener('click', this._boundCaptureZoomIndex, true);

        this.removeEventListener('dragstart', this._boundPreventDragStart);

        this.removeEventListener('pointerdown', this._boundOnPointerDown);
        this.removeEventListener('click', this._boundHandleImageClickZoom, true);
        window.removeEventListener('pointermove', this._boundOnPointerMove);
        window.removeEventListener('pointerup', this._boundOnPointerUp);
        window.removeEventListener('pointercancel', this._boundOnPointerUp);
        window.removeEventListener('modal-trap-activate', this._boundHandleModalTrapActivate);
        window.removeEventListener('load', this._boundPreloadThumbnailsAfterLoad);
        window.removeEventListener('load', this._boundScheduleModalImagePreloadAfterLoad);

        if (this._modalPreloadDelayTimeout !== null) {
          window.clearTimeout(this._modalPreloadDelayTimeout);
          this._modalPreloadDelayTimeout = null;
        }

        if (this._modalPreloadIdleCallback !== null) {
          if ('cancelIdleCallback' in window && typeof this._modalPreloadIdleCallback === 'number') {
            window.cancelIdleCallback(this._modalPreloadIdleCallback);
          } else {
            clearTimeout(this._modalPreloadIdleCallback);
          }
          this._modalPreloadIdleCallback = null;
        }

        for (const btn of this.zoomInButtons) {
          btn.removeEventListener('click', this._boundHandleZoomInClick);
        }
        for (const btn of this.zoomOutButtons) {
          btn.removeEventListener('click', this._boundHandleZoomOutClick);
        }

        this.thumbnailsContainer?.removeEventListener('click', this._boundHandleThumbnailClick);
        this.closeControlsButton?.removeEventListener('click', this._boundHandleCloseControlsClick);
        this.openControlsButton?.removeEventListener('click', this._boundHandleOpenControlsClick);

        if (this.instance) {
          this.instance.destroy(true, true);
          this.instance = null;
        }
      }

      /**
       * Captures zoom index from any matching trap trigger and syncs this gallery to that slide.
       * Matches triggers whose data-focus-trap-toggle equals this gallery's trap id.
       * @param {MouseEvent} e - Click event.
       * @returns {void}
       */
      _captureZoomIndex(e) {
        const trigger =
          e.target instanceof Element ? e.target.closest('[data-focus-trap-toggle][data-zoom-index]') : null;
        if (!trigger) return;

        const trapContainer = this.closest('[data-focus-trap]');
        if (!trapContainer) return;

        const trapId = trapContainer.getAttribute('data-focus-trap');
        const triggerTrapId = trigger.getAttribute('data-focus-trap-toggle');
        if (!trapId || trapId !== triggerTrapId) return;

        const trapSection = trapContainer.closest('section');
        if (trapSection && !trapSection.contains(trigger)) return;

        const raw = trigger.getAttribute('data-zoom-index');
        const index = parseInt(raw ?? '', 10);
        if (Number.isNaN(index)) return;

        this.setAttribute('data-initial-index', String(index));

        if (this.instance) {
          this.#goToIndex(index);
          this.#setActiveThumbnail(index);
          this.#emitZoomIndexChanged(index);

          this.#resetAllSlidesZoom();
          this.#resetPan();
          this.#setZoom(1);
        }
      }

      /**
       * Handles zoom-in button click.
       * @param {MouseEvent} e - Click event.
       * @returns {void}
       */
      _handleZoomInClick(e) {
        e?.preventDefault?.();
        this.#setZoom(this.#getNextZoomLevel());
      }

      /**
       * Handles zoom-out button click.
       * @param {MouseEvent} e - Click event.
       * @returns {void}
       */
      _handleZoomOutClick(e) {
        e?.preventDefault?.();
        this.#setZoom(this.#getPrevZoomLevel());
      }

      /**
       * Re-syncs Swiper when this modal trap becomes active.
       * @param {CustomEvent<{ id: string }>} e - Modal trap activation event.
       * @returns {void}
       */
      _handleModalTrapActivate(e) {
        if (!this.instance) return;

        const trapContainer = this.closest('[data-focus-trap]');
        const trapId = trapContainer?.getAttribute('data-focus-trap');
        if (!trapId || e.detail.id !== trapId) return;

        requestAnimationFrame(() => {
          const activeIndex = this.instance?.activeIndex ?? 0;
          this.instance?.update();
          this.instance?.slideTo(activeIndex, 0);

          this.#syncProductsPanels(activeIndex);
        });
      }

      /**
       * Resets optional product-panel toggle when this modal closes.
       * @param {CustomEvent<{ id: string }>} e - Modal trap deactivation event.
       * @returns {void}
       */
      _handleModalTrapDeactivate(e) {
        const trapContainer = this.closest('[data-focus-trap]');
        const trapId = trapContainer?.getAttribute('data-focus-trap');
        if (!trapId || e.detail?.id !== trapId) return;

        this.#resetProductToggleForClose();
      }

      /**
       * On desktop, click on the active slide image cycles zoom up/down through allowed levels.
       * @param {MouseEvent} e - Click event.
       * @returns {void}
       */
      _handleImageClickZoom(e) {
        if (!window.matchMedia('(min-width: 768px)').matches) return;

        if (this._imageClickZoomBlocked) {
          this._imageClickZoomBlocked = false;
          return;
        }

        const img = this.#getActiveSlideImage();
        if (!img) return;

        const target = e.target instanceof Element ? e.target : null;
        if (!target || (target !== img && !img.contains(target))) return;

        const zoom = this._zoom;
        let nextZoom;

        if (zoom >= this._maxZoom) this._zoomCycleDirection = 'down';
        if (zoom <= this._minZoom) this._zoomCycleDirection = 'up';

        nextZoom = this._zoomCycleDirection === 'up' ? this.#getNextZoomLevel() : this.#getPrevZoomLevel();

        this.#setZoom(nextZoom);
      }

      /**
       * Preloads thumbnail images once the page load has completed.
       * @returns {void}
       */
      _preloadThumbnailsAfterLoad() {
        const thumbnailImages = Array.from(this.thumbnailsContainer?.querySelectorAll('img') ?? []);

        if (!thumbnailImages.length) return;

        for (const image of thumbnailImages) {
          image.loading = 'eager';
          image.decoding = 'async';

          const src = image.currentSrc || image.src;
          if (!src) continue;

          const preloadImage = new Image();

          if (image.srcset) {
            preloadImage.srcset = image.srcset;
          }

          if (image.sizes) {
            preloadImage.sizes = image.sizes;
          }

          preloadImage.src = src;
        }
      }

      /**
       * Defers modal image preloading so it doesn't compete with initial page load.
       * @returns {void}
       */
      _scheduleModalImagePreloadAfterLoad() {
        const navigatorWithConnection =
          /** @type {Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }} */ (navigator);
        const connection = navigatorWithConnection.connection;
        const effectiveType = connection?.effectiveType ?? '';
        const isSlowConnection = Boolean(connection?.saveData) || effectiveType === 'slow-2g' || effectiveType === '2g';
        if (isSlowConnection) return;

        const galleryImages = /** @type {HTMLImageElement[]} */ (
          Array.from(this.querySelectorAll('[data-gallery] .swiper-slide img'))
        );
        if (!galleryImages.length) return;

        const activeIndex = this.instance?.activeIndex ?? 0;
        const preloadQueue = galleryImages
          .map((image, index) => ({ image, distance: Math.abs(index - activeIndex) }))
          .sort((a, b) => a.distance - b.distance)
          .map((item) => item.image);

        const preloadNext = () => {
          const image = preloadQueue.shift();
          if (!image) {
            this._modalPreloadIdleCallback = null;
            return;
          }

          const src = image.currentSrc || image.src;
          if (src) {
            const preloadImage = new Image();
            preloadImage.decoding = 'async';
            preloadImage.fetchPriority = 'low';

            if (image.srcset) preloadImage.srcset = image.srcset;
            if (image.sizes) preloadImage.sizes = image.sizes;

            preloadImage.src = src;
          }

          this.#queueModalPreloadTask(preloadNext);
        };

        if (this._modalPreloadDelayTimeout !== null) {
          window.clearTimeout(this._modalPreloadDelayTimeout);
        }

        this._modalPreloadDelayTimeout = window.setTimeout(() => {
          this._modalPreloadDelayTimeout = null;
          this.#queueModalPreloadTask(preloadNext);
        }, 1000);
      }

      /**
       * Queues a low-priority preload task using idle time when available.
       * @param {() => void} callback - Task callback.
       * @returns {void}
       */
      #queueModalPreloadTask(callback) {
        if ('requestIdleCallback' in window) {
          this._modalPreloadIdleCallback = window.requestIdleCallback(
            () => {
              this._modalPreloadIdleCallback = null;
              callback();
            },
            { timeout: 1500 },
          );
          return;
        }

        this._modalPreloadIdleCallback = setTimeout(() => {
          this._modalPreloadIdleCallback = null;
          callback();
        }, 250);
      }

      /**
       * Binds zoom in/out button click handlers.
       * @returns {void}
       */
      #bindZoomControls() {
        for (const btn of this.zoomInButtons) {
          btn.addEventListener('click', this._boundHandleZoomInClick);
        }
        for (const btn of this.zoomOutButtons) {
          btn.addEventListener('click', this._boundHandleZoomOutClick);
        }
      }

      /**
       * Sets zoom level, updates UI and pan bounds.
       * @param {number} nextZoom - Desired zoom level.
       * @returns {void}
       */
      #setZoom(nextZoom) {
        const clamped = Math.min(this._maxZoom, Math.max(this._minZoom, nextZoom));
        this._zoom = Math.round(clamped * 100) / 100;

        if (this._zoom <= 1) this.#resetPan();

        if (this._zoom === this._minZoom) this._zoomCycleDirection = 'up';
        if (this._zoom === this._maxZoom) this._zoomCycleDirection = 'down';

        this.#clampPanToBounds();

        this.#applyActiveTransform();

        const label = `${Math.round(this._zoom * 100)}%`;
        for (const el of this.zoomValueEls) {
          el.textContent = label;
        }

        const disableOut = this._zoom <= this._minZoom;
        const disableIn = this._zoom >= this._maxZoom;

        for (const btn of this.zoomOutButtons) btn.toggleAttribute('disabled', disableOut);
        for (const btn of this.zoomInButtons) btn.toggleAttribute('disabled', disableIn);
      }

      /**
       * Applies zoom and pan transform to the active slide image.
       * @returns {void}
       */
      #applyActiveTransform() {
        const img = this.#getActiveSlideImage();
        if (!img) return;

        img.setAttribute('draggable', 'false');
        img.style.userSelect = 'none';

        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        img.style.cursor = this._zoom > 1 ? (this._isDragging ? 'grabbing' : 'grab') : isDesktop ? 'grab' : '';

        img.style.touchAction = 'none';

        img.style.transformOrigin = 'center center';
        img.style.willChange = 'transform';
        img.style.transition = this._isDragging ? '' : 'transform 220ms ease';

        img.style.transform = `scale3d(${this._zoom}, ${this._zoom}, 1) translate3d(${this._panX}px, ${this._panY}px, 0)`;
      }

      /** @returns {HTMLImageElement | null} Image in the currently active slide. */
      #getActiveSlideImage() {
        const index = this.instance?.activeIndex ?? 0;
        const slides = this.querySelectorAll('.swiper-slide');
        const slide = slides[index];
        return slide?.querySelector('img') ?? null;
      }

      /** @returns {Element | null} Container element of the currently active slide. */
      #getActiveSlideContainer() {
        const index = this.instance?.activeIndex ?? 0;
        const slides = this.querySelectorAll('.swiper-slide');
        return slides[index] ?? null;
      }

      /** Resets pan offset to zero. \@returns {void} */
      #resetPan() {
        this._panX = 0;
        this._panY = 0;
      }

      /** Clamps pan offset so the image stays within the visible area. \@returns {void} */
      #clampPanToBounds() {
        const img = this.#getActiveSlideImage();
        const container = this.#getActiveSlideContainer();
        if (!img || !container) return;

        const baseW = img.clientWidth;
        const baseH = img.clientHeight;

        const containerW = container.clientWidth;
        const containerH = container.clientHeight;

        if (!baseW || !baseH || !containerW || !containerH) return;

        const scaledW = baseW * this._zoom;
        const scaledH = baseH * this._zoom;

        const maxXVisual = Math.max(0, (scaledW - containerW) / 2);
        const maxYVisual = Math.max(0, (scaledH - containerH) / 2);

        const maxX = maxXVisual / this._zoom;
        const maxY = maxYVisual / this._zoom;

        this._panX = Math.min(maxX, Math.max(-maxX, this._panX));
        this._panY = Math.min(maxY, Math.max(-maxY, this._panY));
      }

      /** Clears zoom/pan styles from all slide images. \@returns {void} */
      #resetAllSlidesZoom() {
        for (const img of this.images) {
          if (!img) continue;
          img.style.transformOrigin = '';
          img.style.transform = '';
          img.style.willChange = '';
          img.style.touchAction = '';
          img.style.userSelect = '';
          img.style.cursor = '';
          img.removeAttribute('draggable');
        }
      }

      /**
       * Handles pointer down for pan drag when zoomed.
       * @param {PointerEvent} e - Pointer event.
       * @returns {void}
       */
      _onPointerDown(e) {
        if (this._zoom <= 1) return;

        const img = this.#getActiveSlideImage();
        if (!img) return;

        const target = e.target;
        const startedOnImg = target === img || (target instanceof Node && img.contains(target));
        if (!startedOnImg) return;

        e.preventDefault();

        this._isDragging = true;
        this._dragDidMove = false;
        this._activePointerId = e.pointerId;

        this._dragStartClientX = e.clientX;
        this._dragStartClientY = e.clientY;
        this._dragStartPanX = this._panX;
        this._dragStartPanY = this._panY;

        img.setPointerCapture?.(e.pointerId);

        this.#applyActiveTransform();
      }

      /**
       * Handles pointer move during pan drag.
       * @param {PointerEvent} e - Pointer event.
       * @returns {void}
       */
      _onPointerMove(e) {
        if (!this._isDragging) return;
        if (this._activePointerId !== e.pointerId) return;

        const dx = e.clientX - this._dragStartClientX;
        const dy = e.clientY - this._dragStartClientY;

        const distance = Math.hypot(dx, dy);
        if (distance >= 5) this._dragDidMove = true;

        this._panX = this._dragStartPanX + dx / this._zoom;
        this._panY = this._dragStartPanY + dy / this._zoom;

        this.#clampPanToBounds();
        this.#applyActiveTransform();
      }

      /**
       * Handles pointer up to end pan drag.
       * @param {PointerEvent} e - Pointer event.
       * @returns {void}
       */
      _onPointerUp(e) {
        if (!this._isDragging) return;
        if (this._activePointerId !== e.pointerId) return;

        if (this._dragDidMove) this._imageClickZoomBlocked = true;
        this._isDragging = false;
        this._activePointerId = null;

        this.#applyActiveTransform();
      }

      /** Syncs modal to the initial slide index from the open trigger. \@returns {void} */
      #syncInitialSlideFromTrigger() {
        if (!this.instance) return;

        const raw = this.getAttribute('data-initial-index');
        if (!raw) return;

        const index = parseInt(raw, 10);
        if (Number.isNaN(index)) return;

        this.#goToIndex(index, { animate: false });
        this.#setActiveThumbnail(index);
        this.#emitZoomIndexChanged(index);

        this.#resetAllSlidesZoom();
        this.#resetPan();
        this.#setZoom(1);
      }

      /**
       * Navigates to the slide at the given index.
       * @param {number} index - Slide index.
       * @param {{ animate?: boolean }} [options] - Slide navigation options.
       * @returns {void}
       */
      #goToIndex(index, options) {
        if (!this.instance) return;

        const animate = options?.animate ?? true;
        const speed = animate ? this.instance.params.speed : 0;

        this.instance.update();
        this.instance.slideTo(index, speed);
      }

      /**
       * Dispatches product-gallery:zoom-index-changed and syncs optional product panels.
       * @param {number} index - Active slide index.
       * @returns {void}
       */
      #emitZoomIndexChanged(index) {
        window.dispatchEvent(
          new CustomEvent('product-gallery:zoom-index-changed', {
            detail: { index },
          }),
        );

        this.#syncProductsPanels(index);
      }

      /**
       * Shows product panels for the active slide when a sibling products container exists.
       * @param {number} index - Active swiper slide index.
       * @returns {void}
       */
      #syncProductsPanels(index) {
        const content = this.#getProductsContent();
        if (!content) return;

        const panels = content.querySelectorAll('[data-modal-gallery-products-slide]');
        if (!panels.length) return;

        for (const panel of panels) {
          const raw = panel.getAttribute('data-modal-gallery-products-slide');
          const slideIndex = parseInt(raw ?? '', 10);
          if (Number.isNaN(slideIndex)) continue;

          panel.toggleAttribute('hidden', slideIndex !== index);
        }
      }

      /** @returns {HTMLElement | null} Optional products panel sibling/container. */
      #getProductsContent() {
        const sibling = this.nextElementSibling;
        if (sibling instanceof HTMLElement && sibling.hasAttribute('data-modal-gallery-products')) {
          return sibling;
        }

        const trap = this.closest('modal-trap');
        const fromTrap = trap?.querySelector('[data-modal-gallery-products]');
        return fromTrap instanceof HTMLElement ? fromTrap : null;
      }

      /**
       * Marks the thumbnail at the given index as active.
       * @param {number} index - Thumbnail index to mark active.
       * @returns {void}
       */
      #setActiveThumbnail(index) {
        const thumbnails = Array.from(this.thumbnailsContainer?.querySelectorAll('[data-gallery-thumbnail]') ?? []);
        if (!thumbnails.length) return;

        for (const thumb of thumbnails) {
          const i = parseInt(thumb.getAttribute('data-index') ?? '', 10);

          if (i === index) {
            thumb.classList.add('modal-media-gallery-thumbnail--active');
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.classList.remove('modal-media-gallery-thumbnail--active');
            thumb.setAttribute('aria-current', 'false');
          }
        }
      }

      /**
       * Handles thumbnail click (used with event delegation for removable listener).
       * @param {MouseEvent} e - Click event.
       * @returns {void}
       */
      _handleThumbnailClick(e) {
        const thumb = e.target instanceof Element ? e.target.closest('[data-gallery-thumbnail]') : null;
        if (!thumb || !this.instance) return;

        const index = parseInt(thumb.getAttribute('data-index') ?? '', 10);
        if (Number.isNaN(index)) return;

        this.#goToIndex(index);
        this.#resetAllSlidesZoom();
        this.#resetPan();
        this.#setZoom(1);
      }

      /** Hides mobile thumbnail controls. \@returns {void} */
      _handleCloseControlsClick() {
        const activeElement = document.activeElement;
        if (activeElement instanceof Node && this.mobileControls?.contains(activeElement)) {
          this.openControlsButton?.focus();
        }

        this.mobileControls?.setAttribute('aria-thumbnails-hidden', 'true');
        this.mobileControls?.setAttribute('aria-hidden', 'true');
        this.mobileControls?.setAttribute('inert', '');
        this.openControlsButton?.setAttribute('aria-controls-open-hidden', 'false');
      }

      /** Shows mobile thumbnail controls. \@returns {void} */
      _handleOpenControlsClick() {
        this.mobileControls?.setAttribute('aria-thumbnails-hidden', 'false');
        this.mobileControls?.removeAttribute('aria-hidden');
        this.mobileControls?.removeAttribute('inert');
        this.openControlsButton?.setAttribute('aria-controls-open-hidden', 'true');

        const firstThumbnail = this.thumbnailsContainer?.querySelector('[data-gallery-thumbnail]');
        if (firstThumbnail instanceof HTMLElement) firstThumbnail.focus();
      }

      /** Binds thumbnail click handler for slide navigation. \@returns {void} */
      #bindThumbnails() {
        const thumbnails = Array.from(this.thumbnailsContainer?.querySelectorAll('[data-gallery-thumbnail]') ?? []);
        if (!this.thumbnailsContainer || !thumbnails.length || !this.instance) return;

        this.thumbnailsContainer.addEventListener('click', this._boundHandleThumbnailClick);
      }

      /** Binds mobile thumbnail show/hide control buttons. \@returns {void} */
      #bindMobileControls() {
        if (this.closeControlsButton && this.mobileControls) {
          this.closeControlsButton.addEventListener('click', this._boundHandleCloseControlsClick);
        }

        if (this.openControlsButton && this.mobileControls) {
          this.openControlsButton.addEventListener('click', this._boundHandleOpenControlsClick);
        }
      }

      /**
       * Returns the next allowed zoom level.
       * @returns {number} Next zoom value from the allowed list.
       */
      #getNextZoomLevel() {
        const currentIndex = this._zoomLevels.indexOf(this._zoom);
        if (currentIndex < 0) return this._zoomLevels[0];
        return this._zoomLevels[Math.min(currentIndex + 1, this._zoomLevels.length - 1)];
      }

      /**
       * Returns the previous allowed zoom level.
       * @returns {number} Previous zoom value from the allowed list.
       */
      #getPrevZoomLevel() {
        const currentIndex = this._zoomLevels.indexOf(this._zoom);
        if (currentIndex < 0) return this._zoomLevels[0];
        return this._zoomLevels[Math.max(currentIndex - 1, 0)];
      }

      /**
       * Clears product-panel toggle (inline display / button labels) so CSS can show
       * the default desktop side-by-side layout or the mobile gallery-first view.
       * @returns {void}
       */
      #resetProductToggleForClose() {
        if (!this._productToggleActive) return;

        this._productToggleActive = false;
        this.style.display = '';

        const content = this._productToggleGalleryContent;
        if (content instanceof HTMLElement) content.style.display = '';

        const originals = this._productToggleButtonOriginals;
        if (!originals) return;

        for (const { el, text } of originals) {
          el.textContent = text;
        }
      }

      /**
       * On desktop, both gallery and products are visible and the toggle is hidden.
       * Clear mobile toggle inline styles when crossing into that breakpoint.
       * @param {MediaQueryListEvent} e - matchMedia change event.
       * @returns {void}
       */
      _handleProductToggleBreakpoint(e) {
        if (!e.matches) return;

        this.#resetProductToggleForClose();
      }

      /**
       * Optional: toggles between gallery and products when trap has [data-product-toggle]
       * and a [data-modal-gallery-products] panel exists.
       * @returns {void}
       */
      #bindProductToggle() {
        const trap = this.closest('modal-trap');
        if (!trap) return;

        const galleryContent = this.#getProductsContent();
        if (!(galleryContent instanceof HTMLElement)) return;

        const toggleButtons = trap.querySelectorAll('[data-product-toggle]');
        if (!toggleButtons.length) return;

        const backLabel = this.getAttribute('data-product-view-back-label') ?? 'Back';

        this._productToggleAbort = new AbortController();
        const { signal } = this._productToggleAbort;

        this._productToggleActive = false;
        this._productToggleGalleryContent = galleryContent;
        this._productToggleButtonOriginals = [];

        for (const toggleButton of toggleButtons) {
          if (!(toggleButton instanceof HTMLElement)) continue;

          const originalButtonText = toggleButton.textContent ?? '';
          this._productToggleButtonOriginals.push({ el: toggleButton, text: originalButtonText });

          toggleButton.addEventListener(
            'click',
            () => {
              this._productToggleActive = !this._productToggleActive;

              if (this._productToggleActive) {
                this.style.display = 'none';
                galleryContent.style.display = 'flex';
                toggleButton.textContent = backLabel;
              } else {
                this.style.display = '';
                galleryContent.style.display = '';
                toggleButton.textContent = originalButtonText;
              }
            },
            { signal },
          );
        }

        window.addEventListener('modal-trap-deactivate', this._boundHandleModalTrapDeactivate, { signal });

        window
          .matchMedia('(min-width: 768px)')
          .addEventListener('change', this._boundHandleProductToggleBreakpoint, { signal });
      }
    },
  );
}
